import Image from 'next/image'

import Grid from '@/components/base/Grid'
import ImageParallax from '@/components/base/ImageParallax'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import { useModal } from '@/contexts/modal'

import { doubleImageRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type DoubleImageProps = {
  image_1: {
    source: WPImage
    gallery_ref: string
  }
  image_2: {
    source: WPImage
    gallery_ref: string
  }
  align?: 'top' | 'bottom-1' | 'bottom-2'
}

const DoubleImage = ({ image_1, image_2, align }: DoubleImageProps) => {
  const { openModalGallery, needsModalGallery } = useModal()

  return (
    <Motion animation={doubleImageRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef}>
          <Grid>
            <div
              className={cn([
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-9 lg:row-start-3 lg:w-col-span-5',
                'grid grid-cols-5 gap-x-gutter-x',
                {
                  'items-start': align === 'top',
                  'items-end': align === 'bottom-1',
                  'items-end lg:row-span-8': align === 'bottom-2'
                }
              ])}
            >
              <div className="col-span-2">
                <div
                  className="overflow-hidden rounded-radius-rounded will-change-transform"
                  data-motion-wrapper
                >
                  <ImageParallax
                    className="relative block aspect-[4/5] h-full w-auto rounded-radius-rounded will-change-transform"
                    data-motion-content
                  >
                    {(ref) => (
                      <span className="absolute left-0 top-0 h-full w-full">
                        <Image
                          ref={ref}
                          src={image_1.source.url}
                          alt={image_1.source.alt}
                          loading="lazy"
                          fill
                          onClick={() => openModalGallery(image_1.gallery_ref)}
                          className={cn([
                            'pointer-events-none rounded-radius-rounded object-cover',
                            {
                              'pointer-events-auto cursor-pointer':
                                needsModalGallery(image_1.gallery_ref)
                            }
                          ])}
                        />
                      </span>
                    )}
                  </ImageParallax>
                </div>
              </div>

              <div className="col-span-3">
                <div
                  className="overflow-hidden rounded-radius-rounded"
                  data-motion-wrapper
                >
                  <ImageParallax
                    className="relative block aspect-[4/5] h-full w-auto rounded-radius-rounded"
                    data-motion-content
                  >
                    {(ref) => (
                      <span className="absolute left-0 top-0 h-full w-full">
                        <Image
                          ref={ref}
                          src={image_2.source.url}
                          alt={image_2.source.alt}
                          fill
                          onClick={() => openModalGallery(image_2.gallery_ref)}
                          className={cn([
                            'pointer-events-none rounded-radius-rounded object-cover',
                            {
                              'pointer-events-auto cursor-pointer':
                                needsModalGallery(image_2.gallery_ref)
                            }
                          ])}
                        />
                      </span>
                    )}
                  </ImageParallax>
                </div>
              </div>
            </div>
          </Grid>
        </Section>
      )}
    </Motion>
  )
}

export default DoubleImage
